<template>
  <DeepMenuUser
    v-model="menu"
    v-model:locale="locale"
    activator-cls="mr-2"
    :available-locales="availableLocales"
    :color="color"
    :is-mobile="device?.isMobile"
    :items="menuItems"
    :user="user"
  />

  <!-- SUPPORT DIALOG -->
  <DialogSupport v-model="showDialogSupport" />
  <!-- /SUPPORT DIALOG -->
</template>

<script lang="ts" setup>
import { links } from '@/constants/links'
import { DeepMenuUser, Flags } from '@deepcloud/deep-ui-lib'
import { useDeepBoxAdminStore } from '@/stores/deepbox/admin/admin.ts'
import { useSettingsDevStore } from '@/stores/settings/settings-dev'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useAppLocale } from '@/composables/app-locale'
import { useAppsUrls } from '@/composables/apps-urls'
import { useKeycloak } from '@/composables/auth/keycloak'
import { useBoxAdmin } from '@/composables/box/box-admin'
import DialogSupport from '@/components/dialogs/DialogSupport.vue'
import { useNotificationsNews } from '@/components/notifications/notifications-news'

defineProps({
  color: {
    type: String,
    default: 'primary',
  },
})

const settingsDevStore = useSettingsDevStore()

const route = useRoute()
const device = inject(DeviceKey)

const { setLocale } = useAppLocale()
const { t, locale, availableLocales } = useI18n()

watch(
  () => locale.value,
  async (newValue) => {
    if (newValue) {
      await setLocale(newValue)
      menu.value = false
    }
  },
)

const { user: userAuth, isAuthenticated, logout } = useKeycloak()

const menu = ref(false)

const user = computed(() => {
  if (isAuthenticated.value) {
    return {
      ...userAuth.value,
      displayName: (userAuth.value?.display_name || userAuth.value?.name) ?? '',
    }
  }
  return undefined
})

const isMobileAppStoreDevice = () =>
  !!device?.isMobile && (device.isIos || device.isAndroid)

const getItemInstallAppPropHref = () => {
  if (device && device.isMobile) {
    if (device.isIos) {
      return import.meta.env.VITE_DEEPBOX_IOS_CLIENT_URL
    }
    if (device.isAndroid) {
      return import.meta.env.VITE_DEEPBOX_ANDROID_CLIENT_URL
    }
  }
  return links.DEEPBOX_WEB_PAGE_DOWNLOAD[
    (locale.value?.toUpperCase() as keyof typeof links.DEEPBOX_WEB_PAGE_DOWNLOAD) ||
      'DE'
  ]
}

const notificationsNews = useNotificationsNews()

const showDialogSupport = ref(false)

watch(
  () => route.hash,
  (newValue) => {
    if (newValue) {
      if (newValue === '#support') {
        showDialogSupport.value = true
      }
    }
  },
  { immediate: true },
)

const deepBoxAdminStore = useDeepBoxAdminStore()
const { adminBox } = storeToRefs(deepBoxAdminStore)
const { adminOrAccessOrgId } = useBoxAdmin(adminBox)
const { getDeepAdminOrgInfoUrl } = useAppsUrls()

const { login } = useKeycloak()

function onLogin() {
  const hostOrigin = window.location.origin
  login({ redirectUri: `${hostOrigin}${route.fullPath}` })
}

const menuItems = computed(() => {
  return [
    {
      key: 'deep_admin',
      title: t('actions.manage_your_account'),
      dataTestId: 'item-deep-admin',
      show: () => !!isAuthenticated.value,
      href: getDeepAdminOrgInfoUrl(adminOrAccessOrgId.value ?? '') ?? '',
      target: '_blank',
    },
    {
      key: 'install_app',
      title: t('actions.install_app'),
      dataTestId: 'item-install-app',
      href: getItemInstallAppPropHref(),
      target: isMobileAppStoreDevice() ? '_self' : '_blank',
      show: () => !!isAuthenticated.value,
      action: () => {
        menu.value = false
      },
    },
    {
      key: 'language',
      title: t('actions.language'),
      dataTestId: 'user-language',
    },
    {
      show: () => !!isAuthenticated.value && settingsDevStore.devModeEnabled,
      divider: true,
      space: 'ma-2',
    },
    {
      key: 'dev-mode',
      title: `DEV mode: ${
        settingsDevStore.devModeEnabled ? 'enabled' : 'disabled'
      }`,
      class: settingsDevStore.devModeEnabled ? 'success--text' : 'error--text',
      prepend: {
        icon: {
          icon: 'far fa-bug',
          color: settingsDevStore.devModeEnabled ? 'success' : 'error',
        },
      },
      dataTestId: 'item-dev-mode',
      show: () => !!isAuthenticated.value && settingsDevStore.isDevModeAllowed,
      action: () => {
        menu.value = false
        settingsDevStore.devShowSettings = !settingsDevStore.devShowSettings
      },
      append: {
        flag: 'DEV' as Flags,
      },
    },
    {
      key: 'dev-news',
      title: `What is new ?`,
      dataTestId: 'item-dev-news',
      prepend: {
        icon: {
          icon: 'far fa-sparkles',
          color: 'yellow',
        },
      },
      show: () =>
        !!isAuthenticated.value && settingsDevStore.canModeFeature('NEWS_LIST'),
      action: () => {
        menu.value = false
        notificationsNews.showDrawer.value = true
      },
      append: {
        flag: 'DEV' as Flags,
      },
    },
    {
      divider: true,
      space: 'ma-2',
    },
    {
      key: 'logout',
      title: t('actions.logout'),
      dataTestId: 'item-logout',
      show: () => !!isAuthenticated.value,
      action: () => logout({}),
    },
    {
      key: 'login',
      title: t('actions.login'),
      dataTestId: 'item-login',
      show: () => !isAuthenticated.value,
      action: () => onLogin(),
    },
  ]
})
</script>
